import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MessageSquare, Heart, Share2, Calendar, Users, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/app', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    // Override mobile app styles ONLY for landing page
    const root = document.getElementById('root');

    // Save original styles
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlHeight = document.documentElement.style.height;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyHeight = document.body.style.height;
    const originalRootOverflow = root?.style.overflow;
    const originalRootHeight = root?.style.height;

    // Apply landing page styles
    document.documentElement.style.cssText = 'overflow: auto !important; height: auto !important;';
    document.body.style.cssText = 'overflow: auto !important; height: auto !important; position: static !important; width: 100% !important;';
    if (root) {
      root.style.cssText = 'overflow: visible !important; height: auto !important; min-height: 100vh !important;';
    }

    return () => {
      // Restore original mobile app styles when leaving landing page
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.height = originalHtmlHeight;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.height = originalBodyHeight;
      if (root) {
        root.style.overflow = originalRootOverflow || '';
        root.style.height = originalRootHeight || '';
      }
    };
  }, []);

  const scrollToFeatures = () => {
    const element = document.getElementById('features');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      className="bg-gradient-to-b from-[#e9d8be] to-white w-full"
      style={{
        overflow: 'visible',
        height: 'auto',
        minHeight: '100vh'
      }}
    >
      {/* Header/Navigation */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img
              src="/app-icon.png"
              alt="KidQuotes Logo"
              className="w-10 h-10"
              style={{ imageRendering: '-webkit-optimize-contrast' }}
            />
            <span className="text-2xl font-bold text-[#2D3A2E]">KidQuotes</span>
          </div>
          <div className="flex gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#2D3A2E]">Sign In</Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-[#8B9A8C] hover:bg-[#7a8a7b] text-white">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-[#2D3A2E] mb-6">
            Capture the Magic of{' '}
            <span className="text-[#8B9A8C]">Childhood</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
            Never forget the funny, sweet, and precious things your kids say.
            Save those special moments that make you smile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/signup">
              <Button size="lg" className="bg-[#8B9A8C] hover:bg-[#7a8a7b] text-white text-lg px-8 py-6">
                Start Capturing Memories
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 text-[#2D3A2E] hover:bg-white/10 bg-white/80 backdrop-blur-sm text-lg px-8 py-6"
              onClick={scrollToFeatures}
            >
              Learn More
            </Button>
          </div>

          {/* App Store Badge */}
          <div className="flex justify-center gap-4 mb-12">
            <a
              href="https://apps.apple.com/app/id6754500015"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-gray-900 transition-colors cursor-pointer"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs">Download on the</div>
                <div className="text-lg font-semibold -mt-1">App Store</div>
              </div>
            </a>
          </div>

          {/* Hero Images/Screenshots */}
          <div className="flex justify-center gap-4 flex-wrap">
            <div className="rounded-2xl shadow-2xl overflow-hidden bg-white border border-gray-200 max-w-xs">
              <img
                src="/app-screenshot.png"
                alt="KidQuotes App Screenshot 1"
                className="w-full h-auto"
              />
            </div>
            <div className="rounded-2xl shadow-2xl overflow-hidden bg-white border border-gray-200 max-w-xs">
              <img
                src="/app-screenshot-2.png"
                alt="KidQuotes App Screenshot 2"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="container mx-auto px-4 py-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2D3A2E] mb-4">
            Everything You Need to Preserve Memories
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Simple, beautiful, and designed for busy parents
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-[#8B9A8C]/10 flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-[#8B9A8C]" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3A2E] mb-2">Capture Quotes Instantly</h3>
                <p className="text-gray-600">
                  Quickly save those hilarious and heartwarming things your kids say before you forget them.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-[#8B9A8C]/10 flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-[#8B9A8C]" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3A2E] mb-2">Track Milestones</h3>
                <p className="text-gray-600">
                  Record important moments and milestones as your children grow, all in one place.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-[#8B9A8C]/10 flex items-center justify-center mb-4">
                  <Share2 className="w-6 h-6 text-[#8B9A8C]" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3A2E] mb-2">Share with Family</h3>
                <p className="text-gray-600">
                  Invite grandparents and family members to enjoy these precious moments together.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-[#8B9A8C]/10 flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-[#8B9A8C]" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3A2E] mb-2">Organize by Child</h3>
                <p className="text-gray-600">
                  Keep memories organized for each of your children with dedicated profiles and timelines.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-[#8B9A8C]/10 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-[#8B9A8C]" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3A2E] mb-2">Family Collaboration</h3>
                <p className="text-gray-600">
                  Both parents can add quotes and memories, building a shared family timeline.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full bg-[#8B9A8C]/10 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-[#8B9A8C]" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3A2E] mb-2">On This Day</h3>
                <p className="text-gray-600">
                  Get reminded of past memories and quotes from previous years on the same day.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-[#8B9A8C] to-[#a4b5a5] rounded-2xl p-12 text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Start Capturing Memories Today
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Join families who are preserving their most precious moments
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-[#2D3A2E] text-white hover:bg-[#1f2a20] text-lg px-8 py-6">
              Create Your Free Account
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-600">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#8B9A8C]" />
            <span className="font-semibold">KidQuotes</span>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-[#8B9A8C] transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-[#8B9A8C] transition-colors">
              Terms of Service
            </Link>
            <Link to="/support" className="hover:text-[#8B9A8C] transition-colors">
              Support
            </Link>
          </div>
          <div className="text-sm">
            © {new Date().getFullYear()} KidQuotes. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
